import { cellStyle } from "../../../components/Table/Table.styles";
import { Member } from "../../../services/api/types/Members";
import { roundToTwo } from "../../../services/utils/roundToTwo";
import Avatar from "../../../assets/Avatar.svg";

// define your filters
export const defaultSliderFilters = [
  { id: 3, title: "Team Members", min: 0, max: 20 },
  { id: 4, title: "Score", min: 0, max: 3 },
  { id: 5, title: "Check-ins Completed", min: 0, max: 150 },
  { id: 6, title: "OT/Wk Last 30", min: 0, max: 200 },
  { id: 7, title: "PTO/Yr", min: 0, max: 200 },
  { id: 8, title: "Hours/Wk Last 30", min: 0, max: 100 },
  { id: 9, title: "Years Employed", min: 0, max: 20 },
];

// define your dropdown filters
export const defaultDropDownFilters = {
  teamName: { id: 1, title: "Teams", options: [] },
  roleName: { id: 2, title: "Roles", options: [] },
};

// define your multi-dropdown filters
export const defaultMultiDropDownFilters = {
  cultureIndex: { id: 10, title: "Culture Index", options: [] },
};

// define your columns
export const getColumns = (token: string) => {
  return [
    {
      title: "Member",
      field: "fullName",
      cellStyle: cellStyle,
      render: (member: Member) => {
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <img
              src={`${process.env.REACT_APP_API_URL}/members/${member.id}/photo?authorization=${token}`}
              onError={event => {
                event.currentTarget.src = Avatar;
                event.currentTarget.onerror = () => undefined;
              }}
              style={{
                marginRight: "9.22px",
                height: "32px",
                width: "40px",
                borderRadius: "7px",
                objectFit: "cover",
              }}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                fontWeight: "600" as any,
                lineHeight: "18px",
                color: "#373F41",
              }}
            >
              <div>{member.fullName}</div>
              {member.teamsLed && member.teamsLed.length > 0 && (
                <div
                  style={{
                    width: "max-content",
                    maxWidth: "200px",
                    whiteSpace: "normal",
                    overflow: "hidden",
                  }}
                >
                  Leads{" "}
                  {member.teamsLed.map((team: any, idx: number) =>
                    idx === member.teamsLed.length - 1 ? team : `${team}, `
                  )}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: "Team",
      field: "teamName",
      searchable: false,
      cellStyle: cellStyle,
      customFilterAndSearch: (value: Array<string>, rowData: any) => {
        if (value[value.length - 1] === "any") {
          return rowData;
        } else {
          return value.indexOf(rowData.teamName) > -1;
        }
      },
      render: (member: any) => {
        let teamName = member.teamName as string;
        if (teamName && teamName.includes(",")) teamName = "Multiple";
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            {teamName}
          </div>
        );
      },
    },
    {
      title: "Role",
      field: "roleName",
      searchable: false,
      cellStyle: cellStyle,
      customFilterAndSearch: (value: Array<string>, rowData: any) => {
        if (value[value.length - 1] === "any") {
          return rowData;
        } else {
          return value.indexOf(rowData.roleName) > -1;
        }
      },
    },
    {
      title: "Team Members",
      field: "numberOfDirectReports",
      searchable: false,
      type: "numeric",
      align: "left",
      cellStyle: cellStyle,
      customFilterAndSearch: (value: Array<number>, rowData: any) =>
        rowData.numberOfDirectReports >= value[0] &&
        rowData.numberOfDirectReports <= value[1],
      render: (member: Member) => {
        return roundToTwo(
          parseFloat(member.numberOfDirectReports as unknown as string)
        );
      },
    },
    {
      title: "Score",
      field: "score",
      searchable: false,
      type: "numeric",
      align: "left",
      cellStyle: cellStyle,
      customFilterAndSearch: (value: Array<number>, rowData: any) => {
        const score = rowData.score || 0;
        return score >= value[0] && score <= value[1];
      },
      render: (member: Member) => {
        return roundToTwo(parseFloat(member.score as unknown as string));
      },
    },
    {
      title: "Check-ins Completed",
      field: "completedCheckinsCount",
      searchable: false,
      type: "numeric",
      align: "left",
      cellStyle: cellStyle,
      customFilterAndSearch: (value: Array<number>, rowData: any) =>
        rowData.completedCheckinsCount >= value[0] &&
        rowData.completedCheckinsCount <= value[1],
      render: (member: Member) => {
        return roundToTwo(
          parseFloat(member.completedCheckinsCount as unknown as string)
        );
      },
    },
    {
      title: "OT/Wk Last 30",
      field: "overtimePerWeek",
      searchable: false,
      type: "numeric",
      align: "left",
      cellStyle: cellStyle,
      customFilterAndSearch: (value: Array<number>, rowData: any) =>
        rowData.overtimePerWeek >= value[0] &&
        rowData.overtimePerWeek <= value[1],
      render: (member: Member) => {
        return roundToTwo(
          parseFloat(member.overtimePerWeek as unknown as string)
        );
      },
    },
    {
      title: "PTO/Yr",
      field: "PTOinHours",
      searchable: false,
      type: "numeric",
      align: "left",
      cellStyle: cellStyle,
      customFilterAndSearch: (value: Array<number>, rowData: any) =>
        rowData.PTOinHours >= value[0] && rowData.PTOinHours <= value[1],
      render: (member: Member) => {
        return roundToTwo(parseFloat(member.PTOinHours as unknown as string));
      },
    },
    {
      title: "Hours/Wk Last 30",
      field: "hoursPerWeek",
      searchable: false,
      type: "numeric",
      align: "left",
      cellStyle: cellStyle,
      customFilterAndSearch: (value: Array<number>, rowData: any) =>
        rowData.hoursPerWeek >= value[0] && rowData.hoursPerWeek <= value[1],
      render: (member: Member) => {
        return roundToTwo(parseFloat(member.hoursPerWeek as unknown as string));
      },
    },
    {
      title: "Years Employed",
      field: "yearsEmployed",
      searchable: false,
      type: "numeric",
      align: "left",
      cellStyle: cellStyle,
      customFilterAndSearch: (value: Array<number>, rowData: any) =>
        rowData.yearsEmployed >= value[0] && rowData.yearsEmployed <= value[1],
      render: (member: Member) => {
        return roundToTwo(
          parseFloat(member.yearsEmployed as unknown as string)
        );
      },
    },
    {
      title: "Culture Index",
      field: "cultureIndex",
      searchable: false,
      align: "left",
      cellStyle: cellStyle,
      customFilterAndSearch: (value: Array<string>, rowData: any) => {
        const selection = value[value.length - 1];
        if (selection === "any") {
          return rowData;
        } else {
          return value.indexOf(rowData.cultureIndex) > -1;
        }
      },
    },
  ];
};
